import {
  Component,
  JSX,
  createMemo,
  createSignal,
  createEffect,
  onMount,
  onCleanup,
  Show,
  For,
} from "solid-js";
import { A, useLocation, useNavigate, useSearchParams } from "@solidjs/router";
import {
  Avatar,
  Badge,
  Dropdown,
  DropdownItem,
  Button,
  Kbd,
  Spinner,
  UpdateNotification,
} from "../ui";
import {
  useProposals,
  useWebSocket,
  useVersionCheck,
  useChangelog,
  useQuotaStatus,
  useProjectQuotaStatus,
} from "../../hooks";
import { ProposalReviewModal } from "../proposals";
import { WhatsNewButton, ChangelogModal } from "../changelog";
import { GlobalQuickCreate } from "./GlobalQuickCreate";
import { QuotaBanner } from "./QuotaBanner";
import { ProjectQuotaBanner } from "./ProjectQuotaBanner";
import { ProjectReadOnlyBanner } from "./ProjectReadOnlyBanner";
import { QuotaPaywallModal } from "../quota";
import { CommandPalette, SearchIcon } from "./CommandPalette";
import {
  createShellKeyHandler,
  NAV_CHORDS,
  modKey,
  needsProject,
} from "./shellShortcuts";
import type {
  ProjectId,
  Project,
  BoardEvent,
  AuthMeResponse,
} from "../../api/types";
import { listProjects } from "../../api/projects";
import { getMe, logout } from "../../api/auth";
import { ChatDrawer, ChatHintTab } from "../chat";
import { WelcomeWizard } from "../onboarding";
import {
  clearOnboardingInFlight,
  readOnboardingInFlight,
} from "../../stores/onboardingResume";
import { useToast } from "../../contexts/ToastContext";
import { ProjectProvider } from "../../contexts/ProjectContext";
import { ChatViewContextProvider } from "../../contexts/ChatViewContext";
import {
  ChatCommandProvider,
  useChatCommand,
} from "../../contexts/ChatCommandContext";
import {
  chatDrawerOpen,
  setChatDrawerOpen,
} from "../../stores/chatDrawerStore";
import {
  commandPaletteOpen,
  setCommandPaletteOpen,
  paletteQuery,
  setPaletteQuery,
} from "../../stores/commandPaletteStore";
import {
  sidebarCollapsed,
  setSidebarCollapsed,
} from "../../stores/sidebarStore";
import {
  themePreference,
  setThemePreference,
  logoSrc,
  type ThemePreference,
} from "../../stores/themeStore";
import {
  currentUser,
  setCurrentUser as publishCurrentUser,
  tenantRole,
} from "../../stores/currentUserStore";
import {
  coalesceUserFetch,
  isUserFetchFresh,
  markSocketDisconnected,
  markUserFetched,
  scheduleThrottledRefresh,
  takeSocketReconnect,
} from "../../stores/authRefreshThrottle";
import {
  canCreateProject,
  isTenantAdmin,
  tenantRoleFor,
} from "../../utils/permissions";

export interface AppShellProps {
  children: JSX.Element;
}

interface NavItem {
  path: string;
  label: string;
  icon: Component<{ class?: string }>;
  disabled?: boolean;
}

/**
 * One run of nav entries under a static label. The canvas leaves the lead
 * section unlabelled, so `label` is null there; every labelled section
 * announces itself as a group through its own heading (FLOW-990).
 */
interface NavSection {
  /** `nav-section-<key>` is the <ul>'s id, which e2e specs select on. */
  key: string;
  label: string | null;
  items: NavItem[];
}

// ============================================
// Icons (inline SVGs to avoid dependencies)
// ============================================

const BoardIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
    />
  </svg>
);

const PlanIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z"
    />
  </svg>
);

const ChatIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

const ReportsIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const MenuIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const ChevronLeftIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

const ChevronRightIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const CheckIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M5 13l4 4L19 7"
    />
  </svg>
);

// Unlike its neighbours above, this one declares `aria-hidden`: its only
// use is decoration beside an aria-label, and the surrounding icons quietly
// drop the attribute their call sites pass.
const SettingsIcon: Component<{
  class?: string;
  "aria-hidden"?: "true";
}> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    aria-hidden={props["aria-hidden"]}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const BacklogIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.125 1.125 0 010 2.25H5.625a1.125 1.125 0 010-2.25z"
    />
  </svg>
);

const DocumentsIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const DecisionsIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 12.75l2.25 2.25 4.5-4.5m4.5 2.25a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const navSections: NavSection[] = [
  {
    key: "plan",
    label: null,
    items: [{ path: "/plan", label: "Plan", icon: PlanIcon }],
  },
  {
    key: "track",
    label: "Track",
    items: [
      { path: "/board", label: "Board", icon: BoardIcon },
      { path: "/backlog", label: "Backlog", icon: BacklogIcon },
    ],
  },
  {
    key: "knowledge",
    label: "Knowledge",
    items: [
      { path: "/documents", label: "Docs", icon: DocumentsIcon },
      { path: "/decisions", label: "Decisions", icon: DecisionsIcon },
    ],
  },
  {
    key: "assist",
    label: "Assist",
    items: [
      { path: "/chat", label: "Chat", icon: ChatIcon },
      { path: "/reports", label: "Reports", icon: ReportsIcon, disabled: true },
    ],
  },
  // No "Projects" entry: the project switcher at the rail's foot owns both
  // project-level destinations (FLOW-989).
];

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: "light", label: "Light theme" },
  { value: "dark", label: "Dark theme" },
  { value: "system", label: "System theme" },
];

export const Header: Component<{
  collapsed: boolean;
  onToggleSidebar: () => void;
  /** Desktop rail collapse. Below md the drawer toggle takes its place. */
  onToggleCollapse: () => void;
  user: AuthMeResponse | null;
  onLogout: () => void;
  hasUnreadChangelog: boolean;
  onOpenChangelog: () => void;
  projectId: ProjectId | undefined;
  projectPrefix: string | undefined;
  onOpenPalette: () => void;
}> = (props) => {
  const navigate = useNavigate();
  const userName = () => props.user?.name ?? "User";
  const avatarUrl = () => props.user?.avatar_url ?? undefined;
  const showLogout = () => props.user?.auth_mode === "oauth";

  return (
    <header class="h-[var(--header-height)] bg-surface border-b border-line flex items-center justify-between px-3 gap-3">
      {/* Left: Mobile menu toggle + Logo */}
      <div class="flex items-center gap-3">
        <button
          onClick={props.onToggleSidebar}
          class="md:hidden p-1.5 text-ink-2 hover:text-ink hover:bg-sunken rounded transition-colors focus-ring"
          aria-label="Toggle sidebar"
        >
          <MenuIcon />
        </button>
        {/* The rail's own collapse control, moved here in FLOW-990. It and
            the drawer toggle above are breakpoint-exclusive, so exactly one
            of them is ever on screen. */}
        <button
          type="button"
          onClick={props.onToggleCollapse}
          class="hidden md:flex p-1.5 text-ink-2 hover:text-ink hover:bg-sunken rounded transition-colors focus-ring"
          aria-label={props.collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeftIcon
            class={
              "w-4 h-4 transition-transform " +
              (props.collapsed ? "rotate-180" : "")
            }
          />
        </button>
        <A
          href="/board"
          class="flex items-center gap-2 rounded transition-opacity hover:opacity-80 focus-ring"
          aria-label="Flowicity home"
        >
          <img src={logoSrc()} alt="" class="w-7 h-7 rounded object-contain" />
          <span
            class="font-semibold text-ink hidden sm:block"
            aria-hidden="true"
          >
            Flowicity
          </span>
        </A>
      </div>

      {/* Center: command palette trigger (FLOW-798) */}
      <div class="flex-1 max-w-md hidden md:block">
        <button
          type="button"
          onClick={props.onOpenPalette}
          aria-label="Search"
          aria-haspopup="dialog"
          aria-keyshortcuts="Meta+K Control+K"
          class="w-full h-7 flex items-center gap-2 px-2.5 text-sm text-ink-3 bg-paper border border-line rounded hover:bg-sunken hover:text-ink-2 transition-colors focus-ring"
        >
          <SearchIcon class="w-4 h-4" />
          <span class="flex-1 text-left">Search</span>
          <Kbd>{modKey()}K</Kbd>
        </button>
      </div>

      {/* Right: Create + What's New + User menu */}
      <div class="flex items-center gap-3">
        <button
          type="button"
          onClick={props.onOpenPalette}
          aria-label="Search"
          aria-haspopup="dialog"
          class="md:hidden p-1.5 text-ink-2 hover:text-ink hover:bg-sunken rounded transition-colors focus-ring"
        >
          <SearchIcon class="w-4 h-4" />
        </button>
        <GlobalQuickCreate
          projectId={props.projectId}
          projectPrefix={props.projectPrefix}
        />
        <WhatsNewButton
          hasUnread={props.hasUnreadChangelog}
          onClick={props.onOpenChangelog}
        />
        <Dropdown
          trigger={
            <button
              class="flex items-center gap-2 py-1 px-2 -mx-1 rounded hover:bg-sunken transition-colors focus-ring"
              aria-label="Account menu"
            >
              <Avatar name={userName()} src={avatarUrl()} size="sm" />
              <span class="text-sm font-medium text-ink hidden sm:block">
                {userName()}
              </span>
            </button>
          }
          class="[&>div:last-child]:right-0 [&>div:last-child]:left-auto"
        >
          <DropdownItem disabled>
            <span class="text-ink-3">Signed in as {userName()}</span>
          </DropdownItem>
          <For each={THEME_OPTIONS}>
            {(opt) => (
              <DropdownItem
                onClick={() => setThemePreference(opt.value)}
                aria-current={
                  themePreference() === opt.value ? "true" : undefined
                }
              >
                <span class="flex items-center gap-2">
                  <span class="font-mono text-ink-3 w-3" aria-hidden="true">
                    {themePreference() === opt.value ? "•" : ""}
                  </span>
                  {opt.label}
                </span>
              </DropdownItem>
            )}
          </For>
          {/* Org billing (FLOW-700). Admin-only: /settings/billing answers 403
              for a member, so the entry would be a permanent link to a dead
              end — the same gate the quota paywall's "View plans" uses. */}
          <Show when={isTenantAdmin(tenantRole())}>
            <DropdownItem onClick={() => navigate("/settings/billing")}>
              Billing
            </DropdownItem>
          </Show>
          <Show when={showLogout()}>
            <DropdownItem onClick={props.onLogout}>
              <span class="text-signal-red">Sign out</span>
            </DropdownItem>
          </Show>
        </Dropdown>
      </div>
    </header>
  );
};

/** 28px row, 14px icon, 9px gap — the canvas rail's density (FLOW-990). */
const NAV_ROW = "flex items-center h-7 gap-[9px] px-2.5 text-sm font-medium";
const NAV_ICON = "w-3.5 h-3.5";

const NavLink: Component<{
  item: NavItem;
  collapsed: boolean;
  /** No project selected yet, so project-scoped entries cannot be reached. */
  projectRequired: boolean;
}> = (props) => {
  const location = useLocation();

  const isActive = () => {
    if (props.item.path === "/board") {
      return location.pathname.startsWith("/board");
    }
    return location.pathname.startsWith(props.item.path);
  };

  const chord = () =>
    Object.entries(NAV_CHORDS).find(
      ([, path]) => path === props.item.path,
    )?.[0];

  // A project-scoped destination with no project selected renders the
  // WelcomeWizard, not the page the entry names (FLOW-985). It becomes the
  // same non-interactive placeholder the not-yet-built entries use, so the
  // rail stops advertising six destinations that all lead to one wizard.
  const blockedByProject = () =>
    props.projectRequired && needsProject(props.item.path);

  // An org member cannot create a project, so telling them to do so would be
  // the same kind of false promise this item is about — the sidebar footer
  // below hides its "+ Create Project" link for exactly that reason, and the
  // WelcomeWizard filling the content area says "Waiting on a project". Read
  // from the store rather than threaded as a prop, as that footer does.
  const blockedReason = () =>
    canCreateProject(tenantRole())
      ? "Create a project first"
      : "Only an organization admin can create a project";

  return (
    <Show
      when={!props.item.disabled && !blockedByProject()}
      fallback={
        <span
          aria-disabled="true"
          class={
            NAV_ROW +
            " rounded text-ink-3 cursor-not-allowed " +
            (props.collapsed ? "justify-center" : "")
          }
          title={
            blockedByProject()
              ? props.collapsed
                ? `${props.item.label} — ${blockedReason()}`
                : blockedReason()
              : props.collapsed
                ? props.item.label
                : undefined
          }
        >
          {props.item.icon({ class: NAV_ICON })}
          <Show when={!props.collapsed}>
            <span>{props.item.label}</span>
            {/* "Soon" is a claim about the roadmap, so it stays on the
                not-yet-built entries only — a blocked entry is finished
                software waiting on a project. */}
            <Show when={props.item.disabled}>
              <Badge variant="default" class="ml-auto text-2xs">
                Soon
              </Badge>
            </Show>
          </Show>
        </span>
      }
    >
      <A
        href={props.item.path}
        aria-current={isActive() ? "page" : undefined}
        aria-keyshortcuts={chord() ? `g ${chord()}` : undefined}
        class={
          "group " +
          NAV_ROW +
          " rounded transition-colors focus-ring " +
          (isActive()
            ? "bg-ink text-paper"
            : "text-ink-2 hover:bg-line hover:text-ink") +
          " " +
          (props.collapsed ? "justify-center" : "")
        }
      >
        {props.item.icon({ class: NAV_ICON })}
        {/* The label stays in the tree even in the icon rail, where it is
            sr-only: it gives the link an accessible name without leaning on a
            title attribute. (aria-current and aria-keyshortcuts above reach
            the anchor fine — the router's <A> spreads its rest props.) */}
        <span class={props.collapsed ? "sr-only" : ""}>{props.item.label}</span>
        <Show when={!props.collapsed && chord()}>
          {(k) => (
            <Kbd
              variant={isActive() ? "bare-inverse" : "bare"}
              aria-hidden="true"
              // Hovering an inactive link darkens its ground to `line`, where
              // ink-3 falls to 3.98:1 — below AA at this size. The active
              // link's ground does not change on hover, so its inverse hint
              // must not follow ink-2 onto ink.
              class={isActive() ? "ml-auto" : "ml-auto group-hover:text-ink-2"}
            >
              G {k().toUpperCase()}
            </Kbd>
          )}
        </Show>
      </A>
    </Show>
  );
};

// ── Project switcher ─────────────────────────────────────────────────────────
// The rail's foot: one row describing the selected project, opening a menu of
// every project plus the two project-level destinations that used to be the
// rail's own "Projects" nav entry.

const projectInitial = (project: Project | undefined) =>
  (project?.prefix?.[0] ?? project?.name?.[0] ?? "?").toUpperCase();

const projectPrefixLabel = (project: Project | undefined) =>
  project?.prefix ?? project?.name?.slice(0, 3).toUpperCase() ?? "—";

// Archived wins over read-only: an archived project is read-only too, and
// naming both would say the same thing twice.
const projectStateLabel = (project: Project | undefined) =>
  project?.archived_at ? "archived" : project?.read_only ? "read-only" : null;

const memberCountLabel = (count: number) =>
  `${count} member${count === 1 ? "" : "s"}`;

// `PREFIX · N members`, with the state marker between them when one applies:
// `PREFIX · archived · N members`. The count is the server's `member_count`
// (FLOW-988), not the number of projects on screen.
const projectSubline = (project: Project | undefined) => {
  const parts = [projectPrefixLabel(project)];
  const state = projectStateLabel(project);
  if (state) parts.push(state);
  // No project selected: there is no membership to count, and "0 members"
  // beside the dash prefix would read as a real project that happens to be
  // empty.
  if (project) parts.push(memberCountLabel(project.member_count ?? 0));
  return parts.join(" · ");
};

// The rail foot with no projects at all. An org admin gets the create
// shortcut; everyone else gets the project list, which is where the
// explanation for the wait lives.
const emptyProjectsLabel = () =>
  canCreateProject(tenantRole()) ? "+ Create Project" : "All projects";

const projectTitle = (project: Project | undefined) => {
  if (!project) return "No project";
  const state = projectStateLabel(project);
  return state ? `${project.name} (${state})` : project.name;
};

// `aria-hidden`, so the initial is not read out ahead of the name beside it.
const ProjectTile: Component<{
  project: Project | undefined;
  class: string;
}> = (props) => (
  <span
    aria-hidden="true"
    class={
      "shrink-0 bg-ink text-paper flex items-center justify-center font-mono font-semibold " +
      props.class
    }
  >
    {projectInitial(props.project)}
  </span>
);

// Two menuitems, not one with a nested button: a menuitem may not contain
// its own focusable descendant, so the settings shortcut only reaches the
// keyboard and the accessibility tree as a row of its own.
const ProjectMenuRow: Component<{
  project: Project;
  current: boolean;
  onSelect: () => void;
  onOpenSettings: () => void;
}> = (props) => (
  <div role="none" class="flex items-stretch">
    <DropdownItem
      onClick={props.onSelect}
      aria-current={props.current ? "true" : undefined}
      class="flex-1 min-w-0 flex items-center gap-2"
    >
      <ProjectTile
        project={props.project}
        class="w-5 h-5 rounded-sm text-[9px]"
      />
      <span
        class={
          "flex-1 min-w-0 truncate text-[11.5px] font-medium " +
          (props.project.archived_at ? "text-ink-3" : "text-ink")
        }
      >
        {props.project.name}
      </span>
      <span class="shrink-0 font-mono text-2xs text-ink-3">
        {projectSubline(props.project)}
      </span>
      {/* Kept in the layout when absent so the names stay on one column. */}
      <CheckIcon
        aria-hidden="true"
        class={"w-3 h-3 shrink-0 " + (props.current ? "text-ink" : "invisible")}
      />
    </DropdownItem>
    {/* Named through aria-label and drawn with an aria-hidden icon, so the
        row carries no second copy of the project name as text — the menu is
        matched on its item text. Reaches the settings of any project in the
        menu, not only the selected one. */}
    <DropdownItem
      onClick={props.onOpenSettings}
      aria-label={`${props.project.name} settings`}
      title={`${props.project.name} settings`}
      class="shrink-0 flex items-center"
    >
      <SettingsIcon aria-hidden="true" class="w-3.5 h-3.5 text-ink-3" />
    </DropdownItem>
  </div>
);

const ProjectSwitcher: Component<{
  collapsed: boolean;
  projects: Project[];
  selectedProjectId: ProjectId | undefined;
  onSelectProject: (id: ProjectId) => void;
}> = (props) => {
  const navigate = useNavigate();

  const selected = () =>
    props.projects.find((p) => p.id === props.selectedProjectId);

  // Deliberately not gated on a role: the settings page is where a member
  // reads the project's context and membership, and it gates its own
  // controls — the same reason every card on /projects links there.
  const openSettings = (id: ProjectId) => navigate(`/projects/${id}/settings`);

  // Archived projects go last, under their own label — they are history, not
  // somewhere work happens.
  const activeProjects = () => props.projects.filter((p) => !p.archived_at);
  const archivedProjects = () => props.projects.filter((p) => p.archived_at);
  const menuProjects = () => [...activeProjects(), ...archivedProjects()];

  let rowsRef: HTMLDivElement | undefined;
  let typeBuffer = "";
  let typeReset: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(typeReset));

  // Typeahead: typing jumps to the first project whose name starts with what
  // has been typed. Dropdown owns Escape and the arrows; this only adds the
  // printable keys, so Space is left alone — it activates the focused row.
  const handleTypeahead = (e: KeyboardEvent) => {
    if (e.key.length !== 1 || e.key === " ") return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;

    e.preventDefault();
    typeBuffer += e.key.toLowerCase();
    clearTimeout(typeReset);
    typeReset = setTimeout(() => {
      typeBuffer = "";
    }, 700);

    const index = menuProjects().findIndex((p) =>
      p.name.toLowerCase().startsWith(typeBuffer),
    );
    if (index < 0) return;
    // The project rows are the menu's first items in `menuProjects()` order,
    // each contributing two menuitems — the row itself and its settings
    // shortcut. "All projects" and "New project" follow them.
    const menuItems =
      rowsRef?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    menuItems?.[index * 2]?.focus();
  };

  return (
    <div class={props.collapsed ? "p-2.5 text-center" : ""}>
      <Dropdown
        placement="top"
        class={props.collapsed ? "" : "w-full"}
        // Wider than the rail in both states, and by more than the shortcut
        // costs. Measured in Chromium at this width: the subline column is
        // 182px on the tightest row ("CAP · read-only · 1 member" — prefix,
        // state marker and FLOW-988's member count), which leaves the name
        // 82px against the 43px "Capped" needs. At 13rem, and at the 20rem
        // that sufficed before the member count joined the subline, that name
        // was ellipsised. `text-left` because the collapsed rail centres its
        // trigger and the panel inherits that — harmless while the menu was
        // rail-width, plainly broken once it is not.
        menuClass="w-[24rem] text-left"
        trigger={(isOpen) => (
          <Show
            when={!props.collapsed}
            fallback={
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={isOpen()}
                aria-label={`Project: ${projectTitle(selected())}`}
                title={projectTitle(selected())}
                class="w-8 h-8 bg-ink text-paper rounded flex items-center justify-center font-mono font-semibold text-xs focus-ring"
              >
                {projectInitial(selected())}
              </button>
            }
          >
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={isOpen()}
              aria-label={`Project: ${projectTitle(selected())}`}
              class="w-full flex items-center gap-2 p-2.5 text-left hover:bg-line transition-colors focus-ring"
            >
              <ProjectTile
                project={selected()}
                class="w-5 h-5 rounded-sm text-[9px]"
              />
              <span class="flex-1 min-w-0">
                <span class="block truncate text-[11.5px] font-medium text-ink">
                  {selected()?.name ?? "No project"}
                </span>
                <span class="block truncate font-mono text-2xs text-ink-3">
                  {projectSubline(selected())}
                </span>
              </span>
              <ChevronRightIcon
                aria-hidden="true"
                class="w-3 h-3 shrink-0 text-ink-3"
              />
            </button>
          </Show>
        )}
      >
        {/* `role="none"` so this keydown host does not land between the menu and
            its items in the accessibility tree. */}
        <div
          role="none"
          // The ref re-fires each time the menu mounts, which is where the
          // typeahead buffer has to be cleared: it outlives the menu
          // otherwise, so reopening within the reset window searches for
          // whatever was last typed plus the new key.
          ref={(el) => {
            rowsRef = el;
            typeBuffer = "";
          }}
          onKeyDown={handleTypeahead}
        >
          <For each={activeProjects()}>
            {(project) => (
              <ProjectMenuRow
                project={project}
                current={project.id === props.selectedProjectId}
                onSelect={() => props.onSelectProject(project.id)}
                onOpenSettings={() => openSettings(project.id)}
              />
            )}
          </For>
          <Show when={archivedProjects().length > 0}>
            {/* A real group, not just a caption: arrow-keying a menu exposes
                only its menuitems, so a visual-only heading would leave a
                screen-reader user with no sense of where the archived rows
                start. The caption itself stays out of the tree. */}
            <div role="group" aria-label="Archived">
              <div
                role="none"
                class="px-3 pt-2 pb-1 font-mono text-2xs uppercase tracking-[0.12em] text-ink-3"
              >
                Archived
              </div>
              <For each={archivedProjects()}>
                {(project) => (
                  <ProjectMenuRow
                    project={project}
                    current={project.id === props.selectedProjectId}
                    onSelect={() => props.onSelectProject(project.id)}
                    onOpenSettings={() => openSettings(project.id)}
                  />
                )}
              </For>
            </div>
          </Show>
          <div role="none" class="my-1 border-t border-line" />
          <DropdownItem onClick={() => navigate("/projects")}>
            All projects
          </DropdownItem>
          {/* The backend refuses a create from an org member, so offering it
              would be the same false affordance the rail avoids elsewhere. */}
          <Show when={canCreateProject(tenantRole())}>
            <DropdownItem onClick={() => navigate("/projects?new=1")}>
              New project
            </DropdownItem>
          </Show>
        </div>
      </Dropdown>
    </div>
  );
};

export const Sidebar: Component<{
  collapsed: boolean;
  mobileOpen: boolean;
  projects: Project[];
  selectedProjectId: ProjectId | undefined;
  onSelectProject: (id: ProjectId) => void;
  /**
   * The project list has loaded and selected nothing, so every
   * project-scoped destination would only render the WelcomeWizard
   * (FLOW-985). Distinct from `!selectedProjectId`, which is also true
   * while the list is still in flight — the rail must not flicker through a
   * disabled state on the way in.
   */
  projectRequired: boolean;
}> = (props) => {
  return (
    <aside
      class={
        "fixed md:static inset-y-0 left-0 z-30 " +
        "bg-sunken border-r border-line " +
        "flex flex-col transition-all duration-200 ease-in-out " +
        // Below md the sidebar is an overlay drawer, off-canvas unless opened;
        // from md up it is always on screen and only its width changes.
        (props.mobileOpen ? "translate-x-0 " : "-translate-x-full ") +
        "md:translate-x-0 " +
        (props.collapsed
          ? "w-[var(--sidebar-collapsed-width)]"
          : "w-[var(--sidebar-width)]")
      }
    >
      {/* Sidebar header. Deliberately empty (FLOW-990 D1): the collapse
          control moved into the app header, but the rail still needs a block
          of the header's own height so the header's bottom hairline reads as
          one unbroken line across the window. */}
      <div
        class="h-[var(--header-height)] border-b border-line"
        aria-hidden="true"
      />

      {/* Navigation items */}
      <nav class="flex-1 py-3 overflow-y-auto">
        <For each={navSections}>
          {(section, index) => (
            <div class={index() > 0 ? "mt-3" : ""}>
              <Show when={section.label}>
                {(label) => (
                  <>
                    {/* The label is a heading, not a control: no section can
                        be collapsed. In the icon rail it stays in the
                        accessibility tree as the group's name while a
                        hairline stands in for it visually. */}
                    <h2
                      id={`nav-section-label-${section.key}`}
                      class={
                        props.collapsed
                          ? "sr-only"
                          : "px-2.5 mb-1 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-ink-3"
                      }
                    >
                      {label()}
                    </h2>
                    <Show when={props.collapsed}>
                      <div
                        class="mx-3 mb-1.5 border-t border-line"
                        aria-hidden="true"
                      />
                    </Show>
                  </>
                )}
              </Show>
              <ul
                id={`nav-section-${section.key}`}
                aria-labelledby={
                  section.label ? `nav-section-label-${section.key}` : undefined
                }
                class="space-y-0.5 px-2"
              >
                <For each={section.items}>
                  {(item) => (
                    <li>
                      <NavLink
                        item={item}
                        collapsed={props.collapsed}
                        projectRequired={props.projectRequired}
                      />
                    </li>
                  )}
                </For>
              </ul>
            </div>
          )}
        </For>
      </nav>

      {/* Sidebar footer - project switcher (FLOW-989) */}
      <div class="border-t border-line">
        <Show
          when={props.projects.length > 0}
          fallback={
            // Nothing to switch between yet, so there is no switcher — but
            // the rail still has to offer the one page that explains the
            // state, because FLOW-989 removed the "Projects" nav entry that
            // used to. An org member cannot create a project, so they get
            // the destination without the promise the backend would refuse —
            // the same split noProjectQuickActions makes in the palette.
            <div class="p-3">
              <Show
                when={!props.collapsed}
                fallback={
                  <A
                    href="/projects"
                    class="w-8 h-8 bg-ink text-paper rounded flex items-center justify-center mx-auto focus-ring"
                    title={emptyProjectsLabel()}
                    aria-label={emptyProjectsLabel()}
                  >
                    <Show
                      when={canCreateProject(tenantRole())}
                      fallback={<BacklogIcon class="w-4 h-4" />}
                    >
                      <span class="font-mono font-semibold text-xs">+</span>
                    </Show>
                  </A>
                }
              >
                <A
                  href="/projects"
                  class="flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-ink hover:bg-line rounded transition-colors focus-ring"
                >
                  {emptyProjectsLabel()}
                </A>
              </Show>
            </div>
          }
        >
          <ProjectSwitcher
            collapsed={props.collapsed}
            projects={props.projects}
            selectedProjectId={props.selectedProjectId}
            onSelectProject={props.onSelectProject}
          />
        </Show>
      </div>
    </aside>
  );
};

interface AIStatusBarProps {
  pendingCount: number;
  isConnected: boolean;
  onShowProposals: () => void;
}

const AIStatusBar: Component<AIStatusBarProps> = (props) => {
  return (
    <footer class="h-[var(--footer-height)] bg-surface border-t border-line px-3 flex items-center justify-between gap-3">
      <div
        role="status"
        aria-label="Connection status"
        class="flex items-center gap-2 font-mono text-xs text-ink-3"
      >
        <span
          aria-hidden="true"
          class={
            "w-2 h-2 rounded-full " +
            (props.isConnected ? "bg-signal-green" : "bg-line-strong")
          }
        />
        <span>{props.isConnected ? "Connected" : "Disconnected"}</span>
      </div>

      {/* `hit-area` because the labels are 16px tall and FLOW-782's guard
          requires a 26px pointer box; the pseudo-element is absolute, so the
          footer's own height is unchanged. */}
      <div class="flex items-center gap-3 font-mono text-xs text-ink-3">
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:text-ink transition-colors focus-ring hit-area"
        >
          Terms
        </a>
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:text-ink transition-colors focus-ring hit-area"
        >
          Privacy
        </a>
      </div>

      <Show when={props.pendingCount > 0}>
        <button
          type="button"
          onClick={props.onShowProposals}
          class="h-7 px-2 inline-flex items-center gap-2 rounded border border-line bg-surface font-mono text-xs text-ink hover:bg-sunken transition-colors focus-ring"
        >
          <span class="text-signal-magenta">ai</span>
          <span>
            {props.pendingCount} pending proposal
            {props.pendingCount !== 1 ? "s" : ""}
          </span>
        </button>
      </Show>
      <Show when={props.pendingCount === 0}>
        <span class="font-mono text-xs text-ink-3">No pending proposals</span>
      </Show>
    </footer>
  );
};

const MobileOverlay: Component<{
  visible: boolean;
  onClick: () => void;
}> = (props) => (
  <Show when={props.visible}>
    <div
      class="fixed inset-0 bg-scrim/50 z-20 md:hidden"
      onClick={props.onClick}
      aria-hidden="true"
    />
  </Show>
);

// ============================================
// ChatCommandBridge — consumes ChatCommandContext inside the provider
// boundary and opens the chat drawer when a command is pending.
// ============================================

const ChatCommandBridge: Component = () => {
  const { pendingCommand } = useChatCommand();

  createEffect(() => {
    if (pendingCommand()) {
      setChatDrawerOpen(true);
    }
  });

  return null;
};

// Shown when the project list fails to load (e.g. a transient 429). Distinct
// from the WelcomeWizard, which is reserved for users who genuinely have no
// projects — a failed load must never look like an empty account.
const ProjectsLoadError: Component<{ onRetry: () => void }> = (props) => (
  <div class="flex h-full items-center justify-center p-4">
    <div class="w-full max-w-md text-center">
      <h2 class="mb-2 text-lg font-semibold text-ink">
        Couldn't load your projects
      </h2>
      <p class="mb-6 text-sm text-ink-2">
        The server is busy or temporarily unavailable. Wait a moment, then try
        again.
      </p>
      <Button onClick={() => props.onRetry()}>Try again</Button>
    </div>
  </div>
);

// Routes that render one record belonging to the selected project. Staying on
// one after a project switch shows the old record under the new project's
// prefix (FLOW-503), so the switch returns to the board instead.
const PROJECT_SCOPED_DETAIL = /^\/(items|documents)\/[^/]+/;

// Surfaces that render their own page with no project selected, so the
// content gate below must let them through instead of swapping in the
// WelcomeWizard (FLOW-985).
export function rendersWithoutProject(pathname: string): boolean {
  // A prefix on purpose: this also covers `/projects/:id/settings`.
  if (pathname.startsWith("/projects")) return true;
  // Billing is tenant-scoped and reads no project context, yet it is linked
  // from the user menu — a new org admin who picked Billing before creating a
  // project used to land on the wizard. Matched exactly rather than by
  // prefix: there is no billing sub-route today, and a prefix would hand this
  // exemption to any future `/settings/billing*` route with no signal, even
  // one that did need a project. Stripe returns to
  // `/settings/billing?checkout=...` and `pathname` excludes the query, so
  // the checkout round-trip still matches.
  return pathname === "/settings/billing";
}

// The `?github_setup=` values the GitHub App callback page can return with.
// Each one leaves the user mid-onboarding with a wizard message to read, so
// all three resume; anything else is an unrelated param on a shared link.
const GITHUB_SETUP_RESULTS = ["success", "cancelled", "requested"];

// The install outcome this page load is a return from, or null if it is not
// one. The router hands a repeated query param back as an array, exactly as
// GitHubAppCallbackPage already normalizes.
function githubSetupResult(
  githubSetup: string | string[] | undefined,
): string | null {
  const setup = Array.isArray(githubSetup) ? githubSetup[0] : githubSetup;
  return setup && GITHUB_SETUP_RESULTS.includes(setup) ? setup : null;
}

// The project whose onboarding should resume after the GitHub App install
// round trip, or null to render normally (FLOW-991).
//
// The marker is client-controlled `localStorage`, so the project is looked up
// in the list the server returned rather than trusted: a forged or stale id
// names nothing and resumes nothing. Pure on purpose — every input is a
// parameter, so the decision is testable without mounting a shell.
export function resumeOnboardingProject(
  projects: Project[],
  inFlightProjectId: string | null,
  githubSetup: string | string[] | undefined,
): Project | null {
  if (!inFlightProjectId) return null;
  if (!githubSetupResult(githubSetup)) return null;
  return projects.find((p) => p.id === inFlightProjectId) ?? null;
}

// Add a project to the list, or replace the entry that already carries its
// id. A resumed onboarding wizard (FLOW-991) finishes with a project the
// shell loaded from the server, so an unconditional append would put two
// options with the same id into the project switcher.
export function mergeProject(projects: Project[], project: Project): Project[] {
  const at = projects.findIndex((p) => p.id === project.id);
  if (at === -1) return [...projects, project];
  const next = [...projects];
  next[at] = project;
  return next;
}

// Where a project switch should leave the user, or null to stay put.
export function projectSwitchDestination(
  pathname: string,
  projectId: ProjectId,
): string | null {
  if (/^\/projects\/[^/]+\/settings/.test(pathname)) {
    return `/projects/${projectId}/settings`;
  }
  return PROJECT_SCOPED_DETAIL.test(pathname) ? "/board" : null;
}

export const AppShell: Component<AppShellProps> = (props) => {
  // Collapse lives in a store so it survives navigation; the mobile drawer is
  // local on purpose, so it closes when a nav link remounts the shell.
  const [mobileOpen, setMobileOpen] = createSignal(false);

  // Auth gate: prevents rendering until a user status is in hand. Since
  // FLOW-984 that status can come from a `currentUserStore` value published
  // within the throttle window instead of a fresh fetch, so the gate confirms
  // the status is recent rather than current-to-the-request; see the onMount
  // skip below for why that is sound.
  const [authChecked, setAuthChecked] = createSignal(false);

  // The user is read straight from `currentUserStore`, with no shell-local
  // copy. A throttled refresh can be scheduled by one shell and fire after
  // the router has disposed it (FLOW-984), so the instance it must update is
  // not the one that asked for it; a local copy would keep rendering the
  // value this mount started with and disagree with the pages, which read
  // the store.

  const [projects, setProjects] = createSignal<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = createSignal<
    ProjectId | undefined
  >(undefined);
  const [projectsLoaded, setProjectsLoaded] = createSignal(false);
  const [projectsError, setProjectsError] = createSignal<Error | null>(null);

  // The account has nothing to work in: the list came back (or failed) and
  // left nothing selected. Everything project-scoped is then unreachable, so
  // the rail and the `g` chords stop offering it (FLOW-985). Gated on
  // `projectsLoaded()` so the initial fetch does not flash a disabled rail,
  // and true on a failed load as well — a project the shell could not load
  // is just as unusable as one that does not exist.
  const projectRequired = createMemo(
    () => projectsLoaded() && !selectedProjectId(),
  );

  const [searchParams] = useSearchParams();
  const toast = useToast();

  // Onboarding that left for the GitHub App install (FLOW-991). Read once,
  // into a plain const, NOT a signal — deliberately non-reactive. The resume
  // memo then flips in the same update as `setProjects`, before any selection
  // exists, so the board never mounts on a resume at all. Make this reactive
  // and the page mounts on the selection and unmounts again when the wizard
  // takes over, which is the double-mount class the content gate below
  // documents.
  const onboardingInFlightId = readOnboardingInFlight();

  const resumedOnboarding = createMemo(() =>
    resumeOnboardingProject(
      projects(),
      onboardingInFlightId,
      searchParams.github_setup,
    ),
  );

  // This load is a return from the install hop, but the resume is
  // impossible — the project is gone. Drop the
  // marker either way, so it cannot arm a surprise resume on some later
  // visit that happens to carry a `?github_setup`; an unrecognized or
  // absent param is NOT a return, and must leave a marker that belongs to a
  // round trip still in flight alone. A successful install is announced on
  // the way past, once per shell — a cancelled one has nothing to reassure
  // anyone about. A list that FAILED is not a strand: the retry can still
  // resume, so saying "go to project settings" there would contradict what
  // the user is about to see.
  let installHandled = false;
  createEffect(() => {
    const setup = githubSetupResult(searchParams.github_setup);
    if (
      projectsLoaded() &&
      !projectsError() &&
      !resumedOnboarding() &&
      onboardingInFlightId &&
      setup &&
      !installHandled
    ) {
      installHandled = true;
      clearOnboardingInFlight();
      if (setup === "success") {
        toast.showSuccess(
          "GitHub App installed. Open project settings to link a repository.",
        );
      }
    }
  });

  // Load (or reload) the project list. Distinguishes "loaded with zero projects"
  // (legitimate new-user onboarding) from "load failed" (e.g. a transient 429) —
  // the latter must NOT fall through to the WelcomeWizard.
  const loadProjects = async () => {
    setProjectsError(null);
    try {
      const resp = await listProjects();
      setProjects(resp.projects);
      if (resp.projects.length > 0) {
        const stored = localStorage.getItem("flowicity_selected_project");
        const match = stored && resp.projects.find((p) => p.id === stored);
        setSelectedProjectId(match ? match.id : resp.projects[0].id);
      }
    } catch (err) {
      setProjectsError(err as Error);
    } finally {
      setProjectsLoaded(true);
    }
  };

  // Fetches the current user, publishes it if active, or sends a
  // no-longer-active one to /pending. Returns whether an active user was
  // published, so the auth gate can decide whether to proceed and the
  // reconnect handler below can ignore it. A getMe() rejection propagates —
  // the auth gate treats that as "stay gated", the reconnect handler
  // swallows it.
  const refreshCurrentUser = async (): Promise<boolean> => {
    // Stamped before the await so two refreshes racing the same window cannot
    // both read a stale timestamp and both issue a request.
    markUserFetched();
    const me = await getMe();
    if (me.status !== "active") {
      window.location.href = "/pending";
      return false;
    }
    publishCurrentUser(me);
    return true;
  };

  // The reconnect path only ever wants the side effects: a 401 is handled by
  // the client's redirect, and any other failure leaves the cached user in
  // place until the next refresh. Note the cue was already consumed when this
  // was scheduled, so a failure is not retried on its own — recovery is the
  // next disconnect/reconnect, or the next mount after the window lapses
  // (pinned by "lets a failed refresh consume the window, then retries once
  // it expires"). That is unchanged from before FLOW-984, which cleared its
  // own flag before the same swallowed call.
  const refreshCurrentUserSwallowing = () => {
    void refreshCurrentUser().catch(() => {});
  };

  onMount(async () => {
    // A user published inside the throttle window is still good enough to
    // open the gate on. This matters because every shell route instantiates
    // its own AppShell and the router disposes the old one on each nav click
    // (see the module-scope throttle state), so re-fetching here spent one
    // strict-budget `/api/auth/me` per navigation — a second drain on the
    // same budget, independent of the reconnect loop and un-throttled besides.
    // Their relative size is not measured; the reconnect storm is what the
    // item was reported for, and both are bounded below. The cached user drives
    // rendering only and every request is authorized server side, so the cost
    // is bounded to staleness: a revocation that closes the socket is picked
    // up by the reconnect refresh below, and one that does not (a plain
    // `users.status` change, a project-level role change — see
    // `authRefreshThrottle`) is picked up by the next mount that finds the
    // window lapsed. Nothing polls, so that is a next-navigation bound and
    // not a time bound: a tab left sitting on one route keeps the stale
    // chrome until it navigates or the socket flaps.
    if (currentUser() && isUserFetchFresh()) {
      setAuthChecked(true);
      await loadProjects();
      return;
    }

    // Fetch current user — gate rendering until auth is confirmed. Routed
    // through coalesceUserFetch so mounts that overlap the very first fetch of
    // a tab share it: the window is stamped before the request goes out but
    // the user is only published after it returns, so the check above cannot
    // see an in-flight one and each such mount would otherwise buy its own.
    try {
      if (!(await coalesceUserFetch(refreshCurrentUser))) return;
    } catch {
      // Will be redirected to login by the 401 handler
      return;
    }
    setAuthChecked(true);

    await loadProjects();
  });

  const location = useLocation();
  const navigate = useNavigate();

  const handleSelectProject = (id: ProjectId) => {
    setSelectedProjectId(id);
    localStorage.setItem("flowicity_selected_project", id);
    const dest = projectSwitchDestination(location.pathname, id);
    if (dest) navigate(dest);
  };

  const handleProjectCreated = (project: Project) => {
    // Merged, not appended: a resumed onboarding wizard (FLOW-991) finishes
    // with a project that is already in the loaded list.
    setProjects((prev) => mergeProject(prev, project));
    setSelectedProjectId(project.id);
    localStorage.setItem("flowicity_selected_project", project.id);
  };

  const currentProjectId = () => selectedProjectId();

  const selectedProjectPrefix = createMemo(() => {
    const id = selectedProjectId();
    if (!id) return undefined;
    const p = projects().find((proj) => proj.id === id);
    return p?.prefix ?? p?.name?.slice(0, 3).toUpperCase() ?? undefined;
  });

  const selectedProject = createMemo(() =>
    projects().find((p) => p.id === selectedProjectId()),
  );

  const selectedProjectTenantId = createMemo(
    () => selectedProject()?.tenant_id,
  );

  // Org role in the SELECTED project's tenant, not the caller's primary one —
  // only an admin of that tenant can act on its plan (FLOW-700).
  const selectedProjectTenantAdmin = createMemo(() =>
    isTenantAdmin(
      tenantRoleFor(currentUser()?.tenants, selectedProjectTenantId()),
    ),
  );

  // Sync project selector when navigating to a project settings page
  createEffect(() => {
    const match = location.pathname.match(/^\/projects\/([^/]+)\/settings/);
    if (match) {
      const urlProjectId = match[1] as ProjectId;
      if (urlProjectId !== selectedProjectId()) {
        setSelectedProjectId(urlProjectId);
        localStorage.setItem("flowicity_selected_project", urlProjectId);
      }
    }
  });

  // Auto-close chat drawer when on /chat page (the page IS the chat)
  createEffect(() => {
    if (location.pathname === "/chat") {
      setChatDrawerOpen(false);
    }
  });

  const toggleChatDrawer = () => {
    const opening = !chatDrawerOpen();
    if (opening) setMobileOpen(false);
    setChatDrawerOpen(opening);
  };

  // Opening the palette over the mobile sidebar would stack two overlays.
  const setPaletteOpen = (open: boolean) => {
    if (open) setMobileOpen(false);
    setCommandPaletteOpen(open);
  };

  // Global keyboard shortcuts: Cmd/Ctrl+J toggles the drawer, Escape closes
  // it, `g` chords navigate, and Cmd/Ctrl+K or `/` opens the command palette.
  //
  // Registered in the capture phase: delegated element handlers (e.g.
  // ChatInput's own Escape handler) are dispatched via a single document
  // bubble-phase listener Solid registers at import time, ahead of anything
  // this component attaches in onMount. A bubble-phase listener here would
  // run after those and could see `e.defaultPrevented` already set by an
  // unrelated handler; capture runs before any of that (FLOW-798).
  onMount(() => {
    const handleKeyDown = createShellKeyHandler({
      navigate,
      toggleDrawer: toggleChatDrawer,
      closeDrawer: () => setChatDrawerOpen(false),
      drawerOpen: chatDrawerOpen,
      paletteOpen: commandPaletteOpen,
      setPaletteOpen,
      paletteQuery,
      setPaletteQuery,
      projectRequired,
    });
    document.addEventListener("keydown", handleKeyDown, true);
    onCleanup(() =>
      document.removeEventListener("keydown", handleKeyDown, true),
    );
  });

  // Proposals management — undefined prevents createResource from fetching
  const safeProjectId = () => selectedProjectId();
  const {
    proposals,
    pendingCount,
    selectedProposal,
    selectedLoading,
    select,
    approve,
    reject,
    refetch,
    handleWebSocketEvent,
  } = useProposals(safeProjectId);

  const quota = useQuotaStatus(selectedProjectTenantId);
  const projectQuota = useProjectQuotaStatus(selectedProjectId);

  const { updateAvailable } = useVersionCheck();

  // Changelog — "What's New" icon and modal
  const {
    hasUnread: hasUnreadChangelog,
    versions: changelogVersions,
    dismiss: dismissChangelog,
  } = useChangelog();
  const [changelogOpen, setChangelogOpen] = createSignal(false);

  const openChangelog = () => {
    setChangelogOpen(true);
    dismissChangelog();
  };

  // WebSocket for real-time updates — forward to both proposals and conversations
  let chatDrawerWsHandler: ((event: BoardEvent) => void) | undefined;
  let chatDrawerNewChatHandler: (() => void) | undefined;

  // A role change (e.g. a tenant admin demoted to member) force-closes this
  // user's socket server-side; a reconnect is our cue that the cached role
  // may now be stale, so re-fetch it. The cue is module-scope state, not a
  // field of this closure, because the disconnect and the reconnect can be
  // observed by two different shell instances (see markSocketDisconnected).
  const { status: wsStatus } = useWebSocket({
    onMessage: (event: BoardEvent) => {
      handleWebSocketEvent(event);
      quota.handleWebSocketEvent(event);
      projectQuota.handleWebSocketEvent(event);
      // Inline proposal refresh on status changes is handled inside ChatDrawer,
      // which owns the conversation's proposal store.
      chatDrawerWsHandler?.(event);
    },
    onStatusChange: (status) => {
      if (status === "disconnected") {
        markSocketDisconnected();
        return;
      }
      if (status === "connected" && takeSocketReconnect()) {
        // A reconnect storm used to issue one `/api/auth/me` per reconnect and
        // exhaust the strict per-IP auth budget (FLOW-984). The throttle bounds
        // that to one refresh per window; the bound is the same order as the
        // server's own 60s membership re-check, and the cached role is a render
        // hint only — every request is authorized server side regardless.
        scheduleThrottledRefresh(refreshCurrentUserSwallowing);
        // A crossing broadcast while the socket was down was never delivered (D4).
        quota.refetch();
        projectQuota.refetch();
      }
    },
  });

  const toggleMobileSidebar = () => {
    setMobileOpen(!mobileOpen());
  };

  const toggleDesktopSidebar = () => {
    // A drawer left open across a resize would otherwise pin the sidebar open.
    setMobileOpen(false);
    setSidebarCollapsed(!sidebarCollapsed());
  };

  const openProposalModal = () => {
    const pending = proposals();
    if (pending && pending.length > 0) {
      select(pending[0].id);
    }
  };

  const handleApprove = async (actionIds?: string[]) => {
    const proposal = selectedProposal();
    if (!proposal) return;
    await approve(proposal.id, actionIds);
    refetch();
  };

  const handleReject = async () => {
    const proposal = selectedProposal();
    if (!proposal) return;
    await reject(proposal.id);
  };

  return (
    <ChatViewContextProvider projectId={selectedProjectId}>
      <ChatCommandProvider projectId={selectedProjectId}>
        {/* Bridge: opens chat drawer when a command is pending */}
        <ChatCommandBridge />

        <div class="h-screen bg-paper flex overflow-hidden relative">
          {/* Auth loading overlay — covers content until user status is confirmed */}
          <Show when={!authChecked()}>
            <div class="absolute inset-0 z-50 bg-paper flex flex-col items-center justify-center gap-4">
              <img
                src={logoSrc()}
                alt="Flowicity"
                class="w-12 h-12 rounded object-contain"
              />
              <Spinner size="lg" class="text-ink" />
            </div>
          </Show>

          {/* Mobile overlay */}
          <MobileOverlay visible={mobileOpen()} onClick={toggleMobileSidebar} />

          {/* Sidebar */}
          <Sidebar
            collapsed={sidebarCollapsed() && !mobileOpen()}
            mobileOpen={mobileOpen()}
            projects={projects()}
            selectedProjectId={currentProjectId()}
            onSelectProject={handleSelectProject}
            projectRequired={projectRequired()}
          />

          {/* Main content area */}
          <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* Header */}
            <Header
              collapsed={sidebarCollapsed()}
              onToggleSidebar={toggleMobileSidebar}
              onToggleCollapse={toggleDesktopSidebar}
              user={currentUser()}
              onLogout={logout}
              hasUnreadChangelog={hasUnreadChangelog()}
              onOpenChangelog={openChangelog}
              projectId={selectedProjectId()}
              projectPrefix={selectedProjectPrefix()}
              onOpenPalette={() => setPaletteOpen(true)}
            />

            <QuotaBanner status={quota.status()} />

            <ProjectQuotaBanner status={projectQuota.status()} />

            <ProjectReadOnlyBanner
              project={selectedProject()}
              isTenantAdmin={selectedProjectTenantAdmin()}
            />

            {/* Page content */}
            <main class="flex-1 overflow-hidden">
              {/* Onboarding interrupted by the GitHub App install picks back
                  up here (FLOW-991). Outermost on purpose: the project it
                  created does exist by now, so the selection arm below would
                  otherwise win and render the board. */}
              <Show
                when={resumedOnboarding()}
                fallback={
                  <Show
                    when={selectedProjectId()}
                    fallback={
                      /* Nothing renders until the list resolves, the exempt
                         paths included. `props.children` sits in both arms of
                         the outer Show, so rendering it here while the fetch
                         is in flight would mount the page once and then
                         remount it when a project arrives and the arm flips —
                         and a page whose `onMount` consumes a one-shot query
                         param loses it to the first mount
                         (`/settings/billing?checkout=pack`). The selection is
                         written before `projectsLoaded`, so for an account
                         that has a project this arm never renders. */
                      <Show when={projectsLoaded()}>
                        <Show
                          when={rendersWithoutProject(location.pathname)}
                          fallback={
                            <Show
                              when={!projectsError()}
                              fallback={
                                <ProjectsLoadError onRetry={loadProjects} />
                              }
                            >
                              <WelcomeWizard
                                onProjectCreated={handleProjectCreated}
                                tenantRole={currentUser()?.tenant_role ?? null}
                              />
                            </Show>
                          }
                        >
                          {props.children}
                        </Show>
                      </Show>
                    }
                  >
                    {(projectId) => {
                      const prefix = () => {
                        const p = projects().find((p) => p.id === projectId());
                        return (
                          p?.prefix ??
                          p?.name?.slice(0, 3).toUpperCase() ??
                          "PRJ"
                        );
                      };
                      return (
                        <ProjectProvider
                          value={{
                            projectId,
                            projectPrefix: prefix,
                            projectTenantId: selectedProjectTenantId,
                            projectQuota: projectQuota.status,
                            onProjectCreated: handleProjectCreated,
                          }}
                        >
                          {props.children}
                        </ProjectProvider>
                      );
                    }}
                  </Show>
                }
              >
                {(project) => (
                  <WelcomeWizard
                    resumeProject={project()}
                    onProjectCreated={handleProjectCreated}
                    tenantRole={currentUser()?.tenant_role ?? null}
                  />
                )}
              </Show>
            </main>

            {/* AI Status Bar */}
            <AIStatusBar
              pendingCount={pendingCount()}
              isConnected={wsStatus() === "connected"}
              onShowProposals={openProposalModal}
            />
          </div>

          {/* Proposal Review Modal */}
          <ProposalReviewModal
            proposal={selectedProposal()}
            loading={selectedLoading()}
            onClose={() => {
              select(null);
              if (chatDrawerOpen()) {
                requestAnimationFrame(() => {
                  const textarea = document.querySelector<HTMLTextAreaElement>(
                    "[data-chat-drawer] textarea",
                  );
                  textarea?.focus();
                });
              }
            }}
            onApprove={handleApprove}
            onReject={handleReject}
            onStartNewChat={() => {
              setChatDrawerOpen(true);
              chatDrawerNewChatHandler?.();
            }}
          />

          {/* Changelog Modal */}
          <ChangelogModal
            open={changelogOpen()}
            onClose={() => setChangelogOpen(false)}
            versions={changelogVersions()}
          />

          {/* Quota paywall (FLOW-795); the selected project names a
              project-ceiling refusal (FLOW-910) */}
          <QuotaPaywallModal
            projectId={selectedProjectId()}
            projectName={selectedProject()?.name}
            projectTenantId={selectedProjectTenantId()}
          />

          {/* Command Palette (FLOW-798) — mounted unconditionally, so it
              still opens with no project selected; with none, only the AI
              row renders (FLOW-799). */}
          <CommandPalette
            projectId={selectedProjectId()}
            projectPrefix={selectedProjectPrefix()}
          />

          {/* selectedProjectId() and the /chat route are genuine "this
              should not exist" cases; chatDrawerOpen() is not — the tab
              stays mounted and moves off-screen via its own `hidden` prop
              so ChatDrawer's focus-restore effect still finds it attached
              when the drawer closes (FLOW-840 I1). */}
          <Show when={selectedProjectId() && location.pathname !== "/chat"}>
            <ChatHintTab onOpen={toggleChatDrawer} hidden={chatDrawerOpen} />
          </Show>

          {/* Chat Drawer */}
          <Show when={selectedProjectId()}>
            {(projectId) => (
              <ChatDrawer
                open={chatDrawerOpen()}
                onClose={() => setChatDrawerOpen(false)}
                projectId={projectId()}
                projectPrefix={selectedProjectPrefix()}
                onApproveProposal={approve}
                onRejectProposal={reject}
                onProposalActioned={() => refetch()}
                onProposalCreated={() => refetch()}
                onRegisterWebSocketHandler={(handler) => {
                  chatDrawerWsHandler = handler;
                }}
                onRegisterNewChat={(handler) => {
                  chatDrawerNewChatHandler = handler;
                }}
                userName={currentUser()?.name}
                userAvatarUrl={currentUser()?.avatar_url}
              />
            )}
          </Show>
        </div>

        {/* Update available toast */}
        <UpdateNotification
          visible={updateAvailable()}
          onReload={async () => {
            // Force fresh HTML into browser cache before reloading
            try {
              await fetch(window.location.href, { cache: "reload" });
            } catch {
              // Proceed with reload even if pre-fetch fails
            }
            window.location.reload();
          }}
        />
      </ChatCommandProvider>
    </ChatViewContextProvider>
  );
};

export default AppShell;
